import { pasteImageAsFrame } from 'clipboard';
import ZoomTool from 'components/Tool/ZoomTool';
import { getBoundingBox, Point, Rect } from 'dragonmouse-shared/geometry';
import { Frame } from 'dragonmouse-shared/interfaces';
import { recordHorizontalWheel, trackZoomJump } from 'hooks/gestureMonitor';
import { clamp } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DropTargetMonitor, useDrop } from 'react-dnd';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectFrames } from 'redux/db/reducers';
import { canEnhanceFrame, getFrame, getFrameDisplayBounds } from 'redux/db/util';
import {
	setCanvasMousePosition,
	setCenterFramesAsChatGeneratesThem,
	setHovered,
	setOffset,
	setScaleAroundMousePosition,
} from 'redux/mode/actions';
import { Pick } from 'redux/mode/classes';
import { selectTransform, ToolType } from 'redux/mode/reducers';
import store from 'redux/store';
import { framePicked, getCanvasRect, getScenePoint, MAX_SCALE, MIN_SCALE } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { CompleteFunction, TargetCanvas } from 'transfer/Target';
import { TransferDragType } from 'transfer/Transfer';
import { TransferBoard } from 'transfer/TransferBoard';
import HandTool from '../Tool/HandTool';
import SelectTool from '../Tool/SelectTool';
import FrameControls from './FrameControls';
import * as S from './styles';

// Multiplicative zoom sensitivity — matches tldraw (deltaY/100) and Excalidraw
// (deltaY*0.01). Smooth across trackpad (small deltas) and mouse wheel (large).
const ZOOM_SENSITIVITY = 0.01;
// Clamp deltaY before applying so trackpad (~1-5) and mouse wheel (~100)
// produce similar zoom feel. tldraw uses MAX_ZOOM_STEP = 10.
const MAX_ZOOM_DELTA = 10;
// Ratio threshold for flagging a single-event zoom jump to telemetry.
const ZOOM_JUMP_RATIO = 3;
// If two wheel events are more than this apart, treat as a new gesture
// and skip jump detection (the first event of a new gesture legitimately
// has no relation to the previous gesture's final scale).
const GESTURE_IDLE_MS = 150;

export interface BoundedPick {
	bounds: Rect;
	pick: Pick;
	canEnhance: boolean;
}

// all interactive widgets etc that relate to the canvas appear here
const UI = () => {
	const dispatch = useDispatch();

	const {
		activeTool,
		hovered,
		selected,
		frames,
		transform,
		zoomPreviewBox,
		activeSceneId,
		centeredFrameId,
	} = useSelector(
		(r: RootReducer) => ({
			activeTool: r.mode.activeTool,
			hovered: r.mode.hovered,
			selected: r.mode.selected,
			frames: selectFrames(r.db),
			transform: selectTransform(r.mode),
			zoomPreviewBox: r.mode.zoomPreviewBox,
			activeSceneId: r.mode.activeSceneId,
			centeredFrameId: r.mode.centeredFrameId,
		}),
		shallowEqual
	);

	const gestureTarget = useRef() as React.MutableRefObject<HTMLDivElement>;

	// variables to handle when the user is panning the canvas
	const [panOverride, setPanOverride] = useState<boolean>(false);
	const [panClickPos, setPanClickPos] = useState<Point>({ x: 0, y: 0 });
	const [panClickOffset, setPanClickOffset] = useState<Point>({ x: 0, y: 0 });

	const [mouseIsOver, setMouseIsOver] = useState<boolean>(false);
	const [centeredFrame, setCenteredFrame] = useState<Frame | null>(null);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	const [{ isOver, canDrop }, drop] = useDrop(
		() => ({
			accept: TransferDragType,
			canDrop: (item, monitor) => {
				return getComplete(item, monitor) != null;
			},
			drop: (item, monitor) => completeDrop(item, monitor),
			collect: (monitor) => ({
				isOver: !!monitor.isOver(),
				canDrop: !!monitor.canDrop(),
			}),
		}),
		[transform.scale]
	);
	// make our gestureTarget ref also function as a drop target
	drop(gestureTarget);

	useEffect(() => {
		if (centeredFrameId === null) {
			setCenteredFrame(null);
			return;
		}
		if (centeredFrameId !== centeredFrame?.id) {
			const frame = getFrame(centeredFrameId);
			if (frame) {
				setCenteredFrame(frame);
			}
		}
	}, [centeredFrameId]);

	//------------------------------------------------------------------------------------------------
	// elements to draw
	// these are stored in scene coordinates
	//------------------------------------------------------------------------------------------------

	// selection rects
	const [selections, selectionBoundingBox] = useMemo(() => {
		const collectedBounds: BoundedPick[] = [];
		for (const pick of selected) {
			if (pick.isFrame()) {
				const frame = getFrame(pick.frameId);
				if (frame) {
					collectedBounds.push({
						bounds: getFrameDisplayBounds(frame),
						pick,
						canEnhance: canEnhanceFrame(frame),
					});
				}
			}
		}
		return [collectedBounds, getBoundingBox(collectedBounds.map((s) => s.bounds))];
	}, [frames, selected]);

	// hover rects
	const hovers: BoundedPick[] = useMemo(() => {
		if (!hovered) return [];

		if (hovered.isFrame()) {
			const frame = getFrame(hovered.frameId);
			if (!frame) return [];

			// if it's already selected, don't show a hover state
			if (framePicked(selected, frame.id)) return [];

			return [
				{
					bounds: getFrameDisplayBounds(frame),
					pick: hovered,
					canEnhance: canEnhanceFrame(frame),
				},
			];
		}

		return [];
	}, [frames, selected, hovered]);

	// Native wheel listener. Ports the tldraw/Excalidraw "unified wheel" pattern:
	// a single handler that branches on ctrlKey || metaKey — macOS synthesizes
	// ctrlKey=true for trackpad pinch, so this unifies pinch-zoom and cmd-wheel-zoom
	// without a stateful gesture library. Reading fresh state via store.getState()
	// (rather than a React closure) closes the stale-scale race that caused the
	// previous "zoom teleports mid-gesture" bug.
	useEffect(() => {
		const el = gestureTarget.current;
		if (!el) return;

		let lastWheelAt = 0;

		const onWheel = (e: WheelEvent) => {
			e.preventDefault();

			const now = performance.now();
			const isNewGesture = now - lastWheelAt > GESTURE_IDLE_MS;
			lastWheelAt = now;

			const rect = el.getBoundingClientRect();
			dispatch(
				setCanvasMousePosition({
					x: e.clientX - rect.left,
					y: e.clientY - rect.top,
				})
			);

			if (e.ctrlKey || e.metaKey) {
				const clampedDelta = clamp(e.deltaY, -MAX_ZOOM_DELTA, MAX_ZOOM_DELTA);
				const factor = Math.exp(-clampedDelta * ZOOM_SENSITIVITY);
				const prev = selectTransform(store.getState().mode).scale;
				const next = clamp(prev * factor, MIN_SCALE, MAX_SCALE);

				if (!isNewGesture && prev > 0) {
					const ratio = next / prev;
					if (ratio > ZOOM_JUMP_RATIO || ratio < 1 / ZOOM_JUMP_RATIO) {
						trackZoomJump({
							prev,
							next,
							ratio,
							deltaY: e.deltaY,
							ctrlKey: e.ctrlKey,
							metaKey: e.metaKey,
						});
					}
				}

				dispatch(setCenterFramesAsChatGeneratesThem(false));
				dispatch(setScaleAroundMousePosition(next));
				return;
			}

			const prevOffset = selectTransform(store.getState().mode).offset;
			dispatch(setCenterFramesAsChatGeneratesThem(false));
			dispatch(
				setOffset({
					x: prevOffset.x - e.deltaX,
					y: prevOffset.y - e.deltaY,
				})
			);

			if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
				recordHorizontalWheel(e.deltaX, e.deltaY);
			}
		};

		el.addEventListener('wheel', onWheel, { passive: false });
		return () => el.removeEventListener('wheel', onWheel);
	}, [dispatch]);

	// Add file drop handling
	useEffect(() => {
		const handleDragOver = (e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();
		};

		const handleDrop = async (e: DragEvent) => {
			e.preventDefault();
			e.stopPropagation();

			const files = Array.from(e.dataTransfer?.files || []);
			const imageFiles = files.filter((file) => file.type.startsWith('image/'));

			if (imageFiles.length > 0) {
				if (!activeSceneId) return;

				const rect = (e.currentTarget as HTMLElement)?.getBoundingClientRect();
				if (!rect) return;

				// Convert drop coordinates to scene coordinates
				const scenePoint = getScenePoint({
					x: e.clientX - rect.left,
					y: e.clientY - rect.top,
				});

				for (const file of imageFiles) {
					await pasteImageAsFrame(
						{
							types: [file.type],
							getType: async () => file,
							presentationStyle: 'inline',
						},
						activeSceneId,
						scenePoint
					);
				}
			}
		};

		const element = gestureTarget.current;
		if (element) {
			element.addEventListener('dragover', handleDragOver);
			element.addEventListener('drop', handleDrop);
		}

		return () => {
			if (element) {
				element.removeEventListener('dragover', handleDragOver);
				element.removeEventListener('drop', handleDrop);
			}
		};
	}, [activeSceneId]);

	//------------------------------------------------------------------------------------------------
	// UTILITY
	//------------------------------------------------------------------------------------------------

	// Return the most appropriate complete function for the provided item (if at least one is available)
	const getComplete = (item: any, monitor: DropTargetMonitor): CompleteFunction | null => {
		const canvas = monitor.getClientOffset();
		if (!canvas) return null;

		if (!(item instanceof TransferBoard)) return null;
		const board: TransferBoard = item;

		const scene = getScenePoint(canvas);

		const target = new TargetCanvas(scene);

		// try to process transfers in order until we've found one
		for (const transfer of board.transfers) {
			const complete = target.getCompleteTransfer(transfer, board);
			if (complete) {
				return complete;
			}
		}
		return null;
	};

	const completeDrop = (item: any, monitor: DropTargetMonitor) => {
		const complete = getComplete(item, monitor);
		if (complete) complete();
	};

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onMouseMove = (event: any) => {
		if (event as MouseEvent) {
			if (panOverride) {
				const dx = event.pageX - panClickPos.x;
				const dy = event.pageY - panClickPos.y;

				dispatch(
					setOffset({
						x: panClickOffset.x + dx,
						y: panClickOffset.y + dy,
					})
				);
			} else {
				const rect = gestureTarget.current.getBoundingClientRect();
				dispatch(
					setCanvasMousePosition({ x: event.clientX - rect.left, y: event.clientY - rect.top })
				);
			}
		}
	};

	const onMouseDown = (event: any) => {
		if ((event as MouseEvent) && event.button === 1) {
			setPanOverride(true);
			setPanClickOffset(transform.offset);
			setPanClickPos({ x: event.pageX, y: event.pageY });
		}
	};

	const onMouseUp = (event: any) => {
		if ((event as MouseEvent) && event.button === 1) {
			setPanOverride(false);
		}
	};

	const onMouseLeave = (_event: any) => {
		setMouseIsOver(false);
		dispatch(setHovered(null));
		setPanOverride(false);
	};

	const onMouseEnter = (_event: any) => {
		setMouseIsOver(true);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderActiveTool = () => {
		if (panOverride) return <S.HandTool />;
		else {
			switch (activeTool) {
				case ToolType.SELECT:
					return <SelectTool />;
				case ToolType.HAND:
					return <HandTool />;
				case ToolType.ZOOM:
					return <ZoomTool />;
				default:
					return null;
			}
		}
	};

	const styleFromRect = (rect: Rect | undefined) => {
		if (!rect) return {};
		return {
			left: rect.x,
			top: rect.y,
			width: rect.width,
			height: rect.height,
		};
	};

	const renderSelection = () => {
		if (centeredFrame) {
			return <FrameControls frame={centeredFrame} />;
		}

		return selections.map((selection, index) => {
			const canvasRect = getCanvasRect(selection.bounds);

			return (
				<React.Fragment key={index}>
					<S.SelectionBox style={styleFromRect(canvasRect)} />
				</React.Fragment>
			);
		});
	};
	const renderSelectionBoundingBox = () => {
		if (centeredFrame) return null;

		if (selectionBoundingBox.width === 0 && selectionBoundingBox.height === 0) return null;
		const canvasRect = getCanvasRect(selectionBoundingBox);
		return <S.SelectionBoundingBox style={styleFromRect(canvasRect)} />;
	};

	const renderHover = () => {
		return hovers.map((hover, index) => {
			return <S.HoverBox key={index} style={styleFromRect(getCanvasRect(hover.bounds))} />;
		});
	};

	const renderZoomPreviewBox = () => {
		if (!zoomPreviewBox) return null;
		return <S.ZoomPreviewBox style={styleFromRect(zoomPreviewBox)} />;
	};

	return (
		<S.UI
			ref={gestureTarget}
			onMouseMove={onMouseMove}
			onMouseDown={onMouseDown}
			onMouseUp={onMouseUp}
			onMouseLeave={onMouseLeave}
			onMouseEnter={onMouseEnter}
			isDropTarget={isOver && canDrop}
		>
			{renderActiveTool()}
			{renderSelection()}
			{renderSelectionBoundingBox()}
			{mouseIsOver && renderHover()}
			{renderZoomPreviewBox()}
		</S.UI>
	);
};

export default UI;
